import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Zap, DollarSign, Gem, Shield, Users, Swords, MapPin,
  Save, Mail, ArrowLeft, Crown, Flame, Star, Trophy,
  ChevronRight, Clock, ShoppingBag, Target, BookOpen,
  Map, Radio, Crosshair, PackageOpen, Gamepad2, Globe
} from 'lucide-react';

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-6">
    <div className="w-10 h-10 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={18} className="text-[#C9A84C]" />
    </div>
    <div>
      <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0]">{title}</h2>
      {subtitle && <p className="font-['Outfit'] text-[#E8E0D0]/40 text-sm mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const InfoCard = ({ title, desc, icon: Icon, accent = '#C9A84C' }) => (
  <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-4 hover:border-[#C9A84C]/20 transition-all">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} style={{ color: accent }} />
      <h4 className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold">{title}</h4>
    </div>
    <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs leading-relaxed">{desc}</p>
  </div>
);

const HowToPlayPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] relative" data-testid="how-to-play-page">
      <div className="film-grain" aria-hidden="true" />

      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-md border-b border-[#C9A84C]/10">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#E8E0D0]/50 hover:text-[#C9A84C] transition-colors" data-testid="back-to-home">
            <ArrowLeft size={16} />
            <span className="font-['Outfit'] text-xs tracking-widest">BACK</span>
          </Link>
          <span className="shrink-0">
            <img src="/kow-game-logo.png" alt="KOW" className="h-8" />
          </span>
          <Link to="/game" className="font-['Outfit'] text-xs tracking-widest text-[#C9A84C] hover:text-[#d4b55d] transition-colors" data-testid="play-now-link">
            PLAY NOW <ChevronRight size={12} className="inline" />
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <motion.div {...fadeIn()} className="text-center mb-16">
          <p className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-[0.3em] mb-3">OFFICIAL GUIDE</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] mb-4" data-testid="how-to-play-title">
            HOW TO PLAY
          </h1>
          <p className="font-['Outfit'] text-[#E8E0D0]/50 text-base max-w-2xl mx-auto leading-relaxed">
            205 territories. 142 street hustles. A live interactive map from Brooklyn to LA. 
            9 playable characters across 3 factions. 4 eras of New York crime history. 
            This is Killing Over Water.
          </p>
        </motion.div>

        {/* THIS IS REAL */}
        <motion.div {...fadeIn(0.1)} className="bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 rounded-lg p-6 mb-12" data-testid="real-money-warning">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#8B1A1A] mb-2">THIS IS A LIVE GAME. REAL MONEY. REAL POWER.</h3>
          <p className="font-['Outfit'] text-[#E8E0D0]/60 text-sm leading-relaxed">
            Killing Over Water uses real USD purchases for Water Crystals, power packs, and premium items. 
            Every dollar translates into in-game power. Players who invest strategically rise faster. There are no refunds on the streets.
          </p>
        </motion.div>

        {/* ── GETTING STARTED ── */}
        <motion.section {...fadeIn(0.15)} className="mb-14" data-testid="section-getting-started">
          <SectionHeader icon={Star} title="GETTING STARTED" subtitle="Choose your faction. Choose your soldier. Enter the streets." />
          <div className="space-y-3">
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-5">
              <h4 className="font-['Outfit'] text-[#C9A84C] text-sm font-semibold mb-3">1. PICK YOUR FACTION</h4>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mb-3">Three factions. Each with their own crew members, alliances, and enemies.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="bg-[#0a0e12] border border-[#C9A84C]/20 rounded p-3">
                  <p className="font-['Outfit'] text-[#C9A84C] text-xs font-bold">CMD</p>
                  <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Coke Money District. Brooklyn's finest. Anton, Vincent, Diego.</p>
                </div>
                <div className="bg-[#0a0e12] border border-[#8B1A1A]/20 rounded p-3">
                  <p className="font-['Outfit'] text-[#8B1A1A] text-xs font-bold">MORELLI FAMILY</p>
                  <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Italian old guard. Francisco, Antwon, Filomena.</p>
                </div>
                <div className="bg-[#0a0e12] border border-[#4A7AB5]/20 rounded p-3">
                  <p className="font-['Outfit'] text-[#4A7AB5] text-xs font-bold">DDN</p>
                  <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">Dominican Distribution Network. Santo, Fuego, La Sombra.</p>
                </div>
              </div>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-5">
              <h4 className="font-['Outfit'] text-[#C9A84C] text-sm font-semibold mb-3">2. PICK YOUR SOLDIER</h4>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mb-3">9 playable characters, each with unique starting stats and storylines.</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { name: 'ANTON', tag: '"The Reaper"', color: '#C9A84C', desc: 'Build CMD from nothing' },
                  { name: 'VINCENT', tag: '"The Brain"', color: '#4A7AB5', desc: 'Strategic empire builder' },
                  { name: 'DIEGO', tag: '"The Ghost"', color: '#4ADE80', desc: 'Invisible operator' },
                  { name: 'FRANCISCO', tag: '"The Old Reaper"', color: '#8B1A1A', desc: 'Morelli patriarch' },
                  { name: 'ANTWON', tag: '"The Bull"', color: '#8B1A1A', desc: 'Kingston enforcer' },
                  { name: 'FILOMENA', tag: 'Morelli', color: '#8B1A1A', desc: 'Ghost by choice' },
                  { name: 'SANTO', tag: '"The Architect"', color: '#4A7AB5', desc: 'DDN mastermind' },
                  { name: 'FUEGO', tag: '', color: '#4A7AB5', desc: 'Reckless and ready' },
                  { name: 'LA SOMBRA', tag: '', color: '#4A7AB5', desc: 'The shadow' },
                ].map(c => (
                  <div key={c.name} className="bg-[#0a0e12] border border-[#1A2535] rounded p-2 text-center">
                    <p className="font-['Outfit'] text-[10px] font-bold" style={{ color: c.color }}>{c.name}</p>
                    <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-5">
              <h4 className="font-['Outfit'] text-[#C9A84C] text-sm font-semibold mb-2">3. ENTER THE STREETS</h4>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm">
                You start in Era 1 — The Foundation (1970s Brooklyn). Run jobs on a real interactive map, buy territory, 
                recruit crew, and work through 4 eras of crime history. Each faction has its own unique crew members. 
                Defeat each era's boss to advance.
              </p>
            </div>
          </div>
        </motion.section>

        {/* ── THE LIVE MAP ── */}
        <motion.section {...fadeIn(0.18)} className="mb-14" data-testid="section-live-map">
          <SectionHeader icon={Map} title="THE LIVE MAP" subtitle="Real streets. Real locations. Zoom in. Pan around. Run your empire." />
          <div className="bg-[#111820] border border-[#4A7AB5]/20 rounded-lg p-6">
            <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm leading-relaxed mb-4">
              Both the <span className="text-[#C9A84C]">Jobs</span> and <span className="text-[#4A7AB5]">Territory</span> tabs 
              use a real interactive map. Every job, every hustle, every territory is pinned to its real-world location. 
              Zoom into East New York for Era 1. Zoom out to see your operations spanning the entire East Coast by Era 4.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-[#0a0e12] border border-[#1A2535] rounded p-3 text-center">
                <p className="font-['Outfit'] text-[#C9A84C] text-lg font-bold">205</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">TERRITORIES</p>
              </div>
              <div className="bg-[#0a0e12] border border-[#1A2535] rounded p-3 text-center">
                <p className="font-['Outfit'] text-[#4A7AB5] text-lg font-bold">142</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">STREET HUSTLES</p>
              </div>
              <div className="bg-[#0a0e12] border border-[#1A2535] rounded p-3 text-center">
                <p className="font-['Outfit'] text-[#4ADE80] text-lg font-bold">48</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">STORY MISSIONS</p>
              </div>
              <div className="bg-[#0a0e12] border border-[#1A2535] rounded p-3 text-center">
                <p className="font-['Outfit'] text-[#9F7AEA] text-lg font-bold">10+</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">STATES COVERED</p>
              </div>
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs mt-3">
              NYC, Newark, Bayonne, Jersey City, Bridgeport, Hartford, Philadelphia, Baltimore, Richmond, Virginia Beach, DC, 
              Atlantic City, Boston, Beverly Hills, Bel Air, Malibu, Inglewood, Compton, Crenshaw, Baldwin Hills, 
              Leimert Park, Ladera Heights, Hollywood Hills, Calabasas, Manhattan Beach, Santa Monica, Venice Beach, and more.
            </p>
          </div>
        </motion.section>

        {/* ── THE CORE SYSTEMS ── */}
        <motion.section {...fadeIn(0.2)} className="mb-14" data-testid="section-five-systems">
          <SectionHeader icon={Target} title="THE CORE SYSTEMS" subtitle="Master them all to dominate." />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoCard icon={Swords} title="JOBS & HUSTLES" accent="#8B1A1A"
              desc="Story missions advance the plot. Street Hustles rotate on a timer — 12 active slots, refreshing every 30 minutes. Common, Uncommon, and Rare tiers. Click any marker on the map to see details and run the job." />
            <InfoCard icon={MapPin} title="TERRITORY" accent="#4A7AB5"
              desc="Buy blocks on the live map to control real locations. Each territory generates passive income every 3 minutes. Own enough blocks to unlock the era boss fight. Tap a territory marker to claim or collect." />
            <InfoCard icon={Users} title="CREW" accent="#4ADE80"
              desc="Each faction has its own unique crew across all 4 eras. CMD gets Tio, Antwon, Goonie. Morelli gets Don Salvatore, Carmine the Butcher. DDN gets Papi Padrino, El Maquina. Recruit them for permanent stat boosts." />
            <InfoCard icon={Shield} title="PROPERTIES & INCOME" accent="#9F7AEA"
              desc="Buy businesses for passive income that generates even when offline. Territory income + property income = your idle economy. Collect it all with one tap on the territory map." />
            <InfoCard icon={Crown} title="ERA BOSS FIGHTS" accent="#C9A84C"
              desc="Defeat the era boss to advance. Bosses get harder across 4 eras. Your crew stats, weapons, and level all matter. Prepare before you fight — or order a hit to skip for $1M." />
            <InfoCard icon={Gamepad2} title="GAME MODES" accent="#F59E0B"
              desc="Dispatch Missions: send crew on auto-missions for rewards. The Numbers: daily betting mini-game. More modes unlock as you progress." />
          </div>
        </motion.section>

        {/* ── INVENTORY & USE ITEMS ── */}
        <motion.section {...fadeIn(0.24)} className="mb-14" data-testid="section-inventory">
          <SectionHeader icon={PackageOpen} title="INVENTORY & ITEMS" subtitle="Every item you buy goes to your stash. USE them when you need them." />
          <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mb-4 leading-relaxed">
            Everything you purchase from Matt's Store is saved to your <span className="text-[#C9A84C]">Inventory</span> tab. 
            Hit the <span className="text-[#E8E0D0]">USE</span> button to activate items when you need them most.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="bg-[#0a0e12] border border-[#8B1A1A]/20 rounded p-3">
              <p className="font-['Outfit'] text-[#8B1A1A] text-[10px] font-bold mb-1">JOBS ITEMS</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Sure Thing (100% success), Job Multiplier (3x cash), Level Skip (+500 XP)</p>
            </div>
            <div className="bg-[#0a0e12] border border-[#4A7AB5]/20 rounded p-3">
              <p className="font-['Outfit'] text-[#4A7AB5] text-[10px] font-bold mb-1">TERRITORY ITEMS</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Block Boost (3x income), Speed Run (1-min cycles), No Touch (protection), Income Surge</p>
            </div>
            <div className="bg-[#0a0e12] border border-[#4ADE80]/20 rounded p-3">
              <p className="font-['Outfit'] text-[#4ADE80] text-[10px] font-bold mb-1">CREW ITEMS</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Enforcer/Earner Temp, Veteran Upgrade (+10 stats), Loyalty Pack, Crew Slot Expand</p>
            </div>
            <div className="bg-[#0a0e12] border border-[#F59E0B]/20 rounded p-3">
              <p className="font-['Outfit'] text-[#F59E0B] text-[10px] font-bold mb-1">ENERGY ITEMS</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Full Tank, Quick Charge, Stamina Boost (+25 max), Unlimited Energy Pass, Double Shift</p>
            </div>
          </div>
          <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs mt-3">
            Weapons and vehicles go to your inventory too — EQUIP them to yourself or a crew member. They stay equipped until you swap them.
          </p>
        </motion.section>

        {/* ── MULTIPLAYER ── */}
        <motion.section {...fadeIn(0.27)} className="mb-14" data-testid="section-multiplayer">
          <SectionHeader icon={Globe} title="MULTIPLAYER: TERRITORY WARS" subtitle="228 real NYC territories. Real players. Real-time battles." />
          <div className="bg-[#111820] border border-[#8B1A1A]/20 rounded-lg p-6">
            <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm leading-relaxed mb-4">
              The multiplayer Territory Wars feature uses a live Leaflet map of NYC with 228 claimable blocks across all 5 boroughs. 
              Claim neutral territories, attack enemy blocks, form crews, declare wars, and communicate in real-time.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <InfoCard icon={MapPin} title="LIVE MAP" accent="#C9A84C" desc="228 real NYC locations. Claim, attack, defend." />
              <InfoCard icon={Users} title="CREWS" accent="#4ADE80" desc="Form crews. Pool resources. Wage wars together." />
              <InfoCard icon={Radio} title="COMMS" accent="#4A7AB5" desc="Global chat, crew chat, DMs. Real-time via WebSockets." />
              <InfoCard icon={Crosshair} title="1v1 CHALLENGES" accent="#8B1A1A" desc="Direct challenges. Wager cash. Winner takes all." />
            </div>
          </div>
        </motion.section>

        {/* ── CURRENCIES ── */}
        <motion.section {...fadeIn(0.3)} className="mb-14" data-testid="section-currencies">
          <SectionHeader icon={DollarSign} title="THE ECONOMY" subtitle="Two currencies. Two speeds. One goal." />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="bg-[#111820] border border-[#4ADE80]/20 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={16} className="text-[#4ADE80]" />
                <h4 className="font-['Outfit'] text-[#4ADE80] text-sm font-bold">STREET CASH</h4>
              </div>
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs leading-relaxed">
                Earned from jobs, territory income, and properties. Used to buy crew, claim territory, 
                and recruit. This is the grind — and with 205 territories generating income, it adds up fast.
              </p>
            </div>
            <div className="bg-[#111820] border border-[#9F7AEA]/20 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-2">
                <Gem size={16} className="text-[#9F7AEA]" />
                <h4 className="font-['Outfit'] text-[#9F7AEA] text-sm font-bold">WATER CRYSTALS</h4>
                <span className="font-['Outfit'] text-[8px] tracking-widest px-1.5 py-0.5 rounded bg-[#8B1A1A]/15 text-[#8B1A1A] border border-[#8B1A1A]/20">REAL USD</span>
              </div>
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-xs leading-relaxed">
                Premium currency ($4.99-$99.99). Buy weapons, power items, dossier fragments, energy, and territory boosts. 
                Water Crystals are power accelerators — skip the grind and hit harder.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="bg-[#0a0e12] border border-[#1A2535] rounded-lg p-3 text-center">
              <Zap size={14} className="text-[#F59E0B] mx-auto mb-1" />
              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-[10px] font-bold">ENERGY</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-0.5">Needed for jobs. Regens over time. Buy boosts.</p>
            </div>
            <div className="bg-[#0a0e12] border border-[#1A2535] rounded-lg p-3 text-center">
              <Star size={14} className="text-[#C9A84C] mx-auto mb-1" />
              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-[10px] font-bold">XP & LEVEL</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-0.5">Level up to unlock higher-tier jobs and territory.</p>
            </div>
            <div className="bg-[#0a0e12] border border-[#1A2535] rounded-lg p-3 text-center">
              <Trophy size={14} className="text-[#C9A84C] mx-auto mb-1" />
              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-[10px] font-bold">RESPECT</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-0.5">Your reputation rank. Affects what you can control.</p>
            </div>
            <div className="bg-[#0a0e12] border border-[#1A2535] rounded-lg p-3 text-center">
              <Flame size={14} className="text-[#8B1A1A] mx-auto mb-1" />
              <p className="font-['Outfit'] text-[#E8E0D0]/60 text-[10px] font-bold">HEAT</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-0.5">Police attention. Manage it or get busted.</p>
            </div>
          </div>
        </motion.section>

        {/* ── BACKPACK MATT'S STORE ── */}
        <motion.section {...fadeIn(0.35)} className="mb-14" data-testid="section-store">
          <SectionHeader icon={ShoppingBag} title="BACKPACK MATT'S STORE" subtitle="Your plug. Your dealer. Your edge." />
          <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mb-4 leading-relaxed">
            Matt is the wandering merchant. His full store has 9 categories — weapons, crew, territory boosts, 
            energy packs, dossier fragments, and power bundles. Everything you buy goes to your Inventory.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoCard icon={Swords} title="WEAPONS ARSENAL" accent="#8B1A1A"
              desc="Era-specific weapons with Water Crystals. Each boosts ATK, DEF, or reduces heat. Equip to yourself or crew members." />
            <InfoCard icon={Gem} title="WATER CRYSTAL PACKS" accent="#9F7AEA"
              desc="500 to 15,000 crystals. $4.99 to $99.99. The backbone of premium power." />
            <InfoCard icon={BookOpen} title="DOSSIER COLLECTION" accent="#C9A84C"
              desc="Collect 5 fragments per character to unlock classified files. 10 collectible characters with hidden lore." />
            <InfoCard icon={Crown} title="MATT'S POWER BUNDLES" accent="#4ADE80"
              desc="Real USD bundles loaded with cash, XP, energy, crystals, and fragments. Stack them for maximum power." />
          </div>
          <div className="bg-[#C9A84C]/5 border border-[#C9A84C]/15 rounded-lg p-4 mt-4">
            <p className="font-['Outfit'] text-[#C9A84C] text-xs leading-relaxed">
              <span className="font-bold">DEAL OF THE DAY:</span> Every day, Matt features one item at 50% off. 
              Check in daily — the deal rotates at midnight.
            </p>
          </div>
        </motion.section>

        {/* ── CHECK-INS ── */}
        <motion.section {...fadeIn(0.38)} className="mb-14" data-testid="section-checkins">
          <SectionHeader icon={Clock} title="DAILY CHECK-INS" subtitle="Show up. Get rewarded. Miss a day = lose your streak." />
          <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mb-3 leading-relaxed">
            Open Matt's Store daily. Four reward tracks run simultaneously. 28 perfect days earns the Water Crown — 
            $250K cash, 250 crystals, +15% income for 7 days. Miss a day? Pay 300 crystals to recover your streak.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-3">
              <p className="font-['Outfit'] text-[#C9A84C] text-[10px] font-bold mb-1">28-DAY STREET RUN</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Daily rewards to Water Crown on Day 28</p>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-3">
              <p className="font-['Outfit'] text-[#4A7AB5] text-[10px] font-bold mb-1">7-DAY EVENT</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Weekly cycle with Flash Drop on Day 7</p>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-3">
              <p className="font-['Outfit'] text-[#4ADE80] text-[10px] font-bold mb-1">8-WEEK REWARD</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Hero Chest at Week 8</p>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-3">
              <p className="font-['Outfit'] text-[#9F7AEA] text-[10px] font-bold mb-1">MONTHLY</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Premium monthly milestone rewards</p>
            </div>
          </div>
        </motion.section>

        {/* ── THE 4 ERAS ── */}
        <motion.section {...fadeIn(0.42)} className="mb-14" data-testid="section-eras">
          <SectionHeader icon={Crown} title="THE FOUR ERAS" subtitle="Over a hundred years. Nine chapters. Coast to coast." />
          <div className="space-y-2">
            {[
              { era: 1, title: 'THE FOUNDATION', sub: 'Raw Survival', years: 'Early-Mid 1970s', location: 'East New York, Brooklyn', boss: 'Slick (Ski Crew)', territories: 30, color: '#C9A84C' },
              { era: 2, title: 'THE RISE', sub: 'Coke Money District', years: 'Late 1970s-Mid 1980s', location: 'Full Brooklyn + Newark, Bayonne, Jersey City', boss: 'Rocky (Flushing Connect)', territories: 38, color: '#4A7AB5' },
              { era: 3, title: 'THE PEAK', sub: 'Money, Power, Respect', years: 'Late 1980s-1990s', location: 'NYC + NJ + CT + VA + PA + DC + LA', boss: 'Francisco Morelli', territories: 64, color: '#8B1A1A' },
              { era: 4, title: 'THE CONSEQUENCES', sub: 'The Waterfront', years: '2020s-Present', location: 'Coast to Coast — Brooklyn to Beverly Hills', boss: 'Vincent Moreno', territories: 73, color: '#9F7AEA' },
            ].map(e => (
              <div key={e.era} className="bg-[#111820] border border-[#1A2535] rounded-lg p-4 hover:border-opacity-40 transition-all" style={{ borderColor: `${e.color}30` }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 border" style={{ backgroundColor: `${e.color}10`, borderColor: `${e.color}30` }}>
                    <span className="font-['Cormorant_Garamond'] text-xl font-bold" style={{ color: e.color }}>{e.era}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-['Outfit'] text-[#E8E0D0] text-sm font-semibold">{e.title}</h4>
                      <span className="font-['Outfit'] text-[8px] tracking-widest px-1.5 py-0.5 rounded border" style={{ color: `${e.color}99`, borderColor: `${e.color}30`, backgroundColor: `${e.color}10` }}>{e.sub.toUpperCase()}</span>
                    </div>
                    <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs mt-0.5">{e.years} — {e.location}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs">{e.territories} blocks</p>
                    <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">Boss: {e.boss}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* ── POST-GAME ── */}
        <motion.section {...fadeIn(0.45)} className="mb-14" data-testid="section-postgame">
          <SectionHeader icon={Flame} title="AFTER THE SAGA" subtitle="Beat the game? The streets don't stop." />
          <div className="bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-lg p-6">
            <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm leading-relaxed mb-3">
              When you defeat the final boss, you get three options: <span className="text-[#C9A84C]">Continue Playing</span>, 
              Return to the Water, or start a New Path. Continue Playing unlocks post-game mode:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="bg-[#111820] border border-[#C9A84C]/15 rounded p-3">
                <p className="font-['Outfit'] text-[#C9A84C] text-[10px] font-bold">20 HUSTLE SLOTS</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Up from 12. More jobs available at once.</p>
              </div>
              <div className="bg-[#111820] border border-[#C9A84C]/15 rounded p-3">
                <p className="font-['Outfit'] text-[#C9A84C] text-[10px] font-bold">10-MIN ROTATION</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Hustles refresh 3x faster. Always fresh ops.</p>
              </div>
              <div className="bg-[#111820] border border-[#C9A84C]/15 rounded p-3">
                <p className="font-['Outfit'] text-[#C9A84C] text-[10px] font-bold">PRESTIGE MODE</p>
                <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">Restart with harder difficulty. Keep crystals.</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── SAVING ── */}
        <motion.section {...fadeIn(0.48)} className="mb-14" data-testid="section-saving">
          <SectionHeader icon={Save} title="SAVING & RESUMING" subtitle="Your empire doesn't disappear." />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#111820] border border-[#4ADE80]/20 rounded-lg p-4">
              <Mail size={14} className="text-[#4ADE80] mb-2" />
              <p className="font-['Outfit'] text-[#4ADE80] text-xs font-bold mb-1">EMAIL SAVE</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px]">Enter email at game start. Resume from any device.</p>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-4">
              <Save size={14} className="text-[#C9A84C] mb-2" />
              <p className="font-['Outfit'] text-[#C9A84C] text-xs font-bold mb-1">AUTO-SAVE</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px]">Game auto-saves to browser. Continue or Start New.</p>
            </div>
            <div className="bg-[#111820] border border-[#1A2535] rounded-lg p-4">
              <Clock size={14} className="text-[#9F7AEA] mb-2" />
              <p className="font-['Outfit'] text-[#9F7AEA] text-xs font-bold mb-1">OFFLINE INCOME</p>
              <p className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px]">Properties and territory earn while you're away.</p>
            </div>
          </div>
        </motion.section>

        {/* ── TIPS ── */}
        <motion.section {...fadeIn(0.5)} className="mb-16" data-testid="section-tips">
          <SectionHeader icon={Target} title="STREET RULES" subtitle="Wisdom from the water." />
          <div className="bg-[#111820] border border-[#C9A84C]/10 rounded-lg p-6">
            <div className="space-y-3">
              {[
                "Use the live map. Zoom in to find jobs near each other for efficient grinding.",
                "Buy territory early and often. 205 blocks means massive passive income potential.",
                "Each faction has unique crew. Morelli gets Italian muscle. DDN gets Dominican street power. Choose wisely.",
                "Tap COLLECT ALL on the territory map regularly. Don't leave money sitting.",
                "Street Hustles rotate every 30 min (10 min post-game). Rare jobs pay big — watch for red markers.",
                "Energy items stack. Full Tank + Double Shift = grind for hours without stopping.",
                "Don't fight the era boss underprepared. Level up, equip weapons, stack your crew first.",
                "Check in to Matt's Store daily. The 28-day Water Crown is worth it.",
                "After beating the game, hit CONTINUE PLAYING. Post-game unlocks 20 hustles and faster rotation.",
                "Save with email. Browser data can be cleared — email saves are forever.",
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="font-['Outfit'] text-[#C9A84C]/40 text-xs font-bold mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm">{tip}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div {...fadeIn(0.55)} className="text-center">
          <Link to="/game"
            className="inline-block bg-[#C9A84C] text-[#080C10] py-3.5 px-10 rounded font-['Outfit'] font-bold text-sm tracking-widest hover:bg-[#d4b55d] transition-colors"
            data-testid="cta-play-now">
            ENTER THE STREETS
          </Link>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/20 italic text-sm mt-4">
            "The water remembers every move you make."
          </p>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#C9A84C]/5 mt-12 py-6 text-center">
        <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[10px] tracking-widest">
          KILLING OVER WATER — A TASTE OF NEW YORK ENTERTAINMENT PRODUCTION
        </p>
      </div>
    </div>
  );
};

export default HowToPlayPage;
